import {
  getTherapy,
  getTherapyAndTypeNameAsString,
  getTherapyType,
  IAttachment,
  IPatient,
  Therapy,
} from '@alberta/konexi-shared';
import { Injectable } from '@angular/core';
import { AuthService } from '@services/auth.service';
import { BodyPartService } from '@services/body-part.service';
import { PatientService } from '@services/patient.service';
import { cloneDeep } from 'lodash';
import { v4 } from 'uuid';
import { AttachmentDto } from '../../models/attachment/attachment-dto.model';
import { AttachmentMetadata } from '../../models/attachment/attachment-meta-data.model';
import { base64ToBlob, getMimeTypeFromBase64String, stripDataPartFromBase64String } from './attachment-helper';
import { ATTACHMENT_LOCAL_PREFIX } from './attachment-service.interface';
import { UploadResult } from './attachment-transfer.service';
import { AttachmentTypeService } from './attachment-type.service';
import { CreateMetadata, UploadReadyMetadata } from './attachment.service';

@Injectable({ providedIn: 'root' })
export class AttachmentUtilsService {
  constructor(
    private _auth: AuthService,
    private _attachmentTypeService: AttachmentTypeService,
    private _bodyPartService: BodyPartService,
    private _patientService: PatientService
  ) {}

  async fetchAndProcessAttachment(
    feathersService: any,
    attachmentId: string,
    setAttachmentDisplayData: (attachment: any) => Promise<void>
  ): Promise<{ attachment: AttachmentDto; pdf?: Blob; imageData?: string }> {
    const result = await feathersService.find({
      query: { _id: { $in: [attachmentId] } },
    });

    if (result?.data?.length) {
      const attachment = result.data[0];
      await setAttachmentDisplayData(attachment);

      if (attachment.contentType.includes('image')) {
        const imageSource = await feathersService.get(attachmentId);
        const { base64 } = this.convertArrayBuffer(imageSource, 'base64', attachment.contentType);
        return { attachment, imageData: base64 };
      } else {
        const pdfArraybuffer = await feathersService.get(attachmentId);
        const pdf = new Blob([pdfArraybuffer], { type: 'application/pdf' });
        return { attachment, pdf };
      }
    } else {
      throw new Error(`Attachment result empty`);
    }
  }

  /**
   * Converts an ArrayBuffer to either base64 string or PDF blob
   * @param buffer - The ArrayBuffer to convert
   * @param type - Conversion type: 'base64' for base64 string only, 'pdf' for both blob URL and base64
   * @param contentType - The MIME type of the content
   * @returns Object containing base64 string and/or blob URL depending on the requested type
   */
  public convertArrayBuffer(
    buffer: ArrayBuffer,
    type: 'base64' | 'pdf',
    contentType: string
  ): { base64?: string; blobUrl?: string } {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    bytes.forEach(byte => {
      binary += String.fromCharCode(byte);
    });

    if (type === 'base64') {
      return {
        base64: 'data:' + contentType + ';base64,' + window.btoa(binary),
      };
    } else {
      const blob = new Blob([bytes], { type: contentType });
      return {
        blobUrl: URL.createObjectURL(blob),
        base64: 'data:' + contentType + ';base64,' + window.btoa(binary),
      };
    }
  }

  public getTherapyName(attachment: IAttachment): string {
    const therapyId = attachment?.metadata?.therapyId;
    const therapyTypeId = attachment?.metadata?.therapyTypeId;
    let therapyName = getTherapyAndTypeNameAsString(therapyId, therapyTypeId);

    if (therapyId === Therapy.WV && attachment?.metadata?.woundLocation !== 'undefined') {
      const woundLocation = parseInt(attachment?.metadata?.woundLocation, 10);
      const woundLocationName = this._bodyPartService.getFullBodyPartName(woundLocation);
      therapyName += `, ${woundLocationName}`;
    }
    return therapyName;
  }

  public async getDisplayFileName(patient: IPatient, attachment: IAttachment): Promise<string> {
    if (!attachment || !patient) {
      return Promise.resolve('');
    }

    const createdAt = new Date(attachment.metadata.createdAt).toISOString().substring(0, 10);

    const fileExtension = attachment.filename.substring(
      attachment.filename.lastIndexOf('.') + 1,
      attachment.filename.length
    );

    const attachmentType = await this._attachmentTypeService.getTranslation(attachment?.metadata?.type);

    let therapyName = '';
    const therapyItem = getTherapy(attachment?.metadata?.therapyId);
    if (therapyItem && therapyItem.id > 0) {
      therapyName += `_${therapyItem.displayName}`;

      const therapyType = getTherapyType(attachment?.metadata?.therapyId, attachment?.metadata?.therapyTypeId);
      if (therapyType) {
        therapyName += `_${therapyType.displayName}`;
      }
      return `${attachmentType}_${patient.firstName}_${patient.lastName}${therapyName}_${createdAt}.${fileExtension}`;
    } else {
      return `${attachmentType}_${patient.firstName}_${patient.lastName}_${createdAt}.${fileExtension}`;
    }
  }

  public async createMetaData(
    patient: IPatient,
    data: { image: string } & Record<string, any>,
    relatedEntity?: AttachmentMetadata['relatedEntity']
  ): Promise<CreateMetadata> {
    const { image, ...meta } = data;
    meta.patientId = patient._id;
    meta.regionId = patient.regionId;
    meta.archived = false;
    meta.createdBy = await this._auth.getCurrentUserId();
    if (relatedEntity) {
      meta.relatedEntity = relatedEntity;
    }
    const fileExtension = image.substring(image.indexOf('/') + 1, image.indexOf(';base64'));
    if (!meta.name) {
      const attachmentTypeName = await this._attachmentTypeService.getTranslation(meta.type);
      const timestamp = new Date().toISOString().substring(0, 10);
      meta.name = `${attachmentTypeName}_${patient.firstName}_${patient.lastName}_${timestamp}.${fileExtension}`;
    }

    return meta as CreateMetadata;
  }

  public async createMedicalOrderMetaData(
    patient: IPatient,
    data: { image: string } & Record<string, any>,
    medicalOrderId: string
  ): Promise<CreateMetadata & { medicalOrderId: string }> {
    const meta = await this.createMetaData(patient, data, 'medicalOrder' as AttachmentMetadata['relatedEntity']);
    return { ...meta, medicalOrderId };
  }

  public async createSingleOrderMetaData(
    patient: IPatient,
    data: { image: string } & Record<string, any>,
    singleOrderId: string
  ): Promise<CreateMetadata & { singleOrderId: string }> {
    const meta = await this.createMetaData(patient, data, 'singleOrder');
    return { ...meta, singleOrderId };
  }

  public async prepareAttachmentData(base64String: string, metadata: CreateMetadata) {
    // Process the base64 string
    let base64DataInitial: string = stripDataPartFromBase64String(base64String);
    if (base64DataInitial.indexOf('data:') !== -1) {
      base64DataInitial = stripDataPartFromBase64String(base64DataInitial);
    }
    const base64Data = base64DataInitial;

    // Generate MIME type and Blob
    const mime: string = getMimeTypeFromBase64String(base64String);
    const blob: Blob = base64ToBlob(base64Data, mime);

    // Generate metadata
    const uploadMetadata = this._getUploadReadyMetadata(metadata);

    // Create filename
    const currentDate = new Date();
    const fileExtension = metadata.name
      ? metadata.name.substring(metadata.name.lastIndexOf('.') + 1, metadata.name.length)
      : '';
    const attachmentType = uploadMetadata.type
      ? await this._attachmentTypeService.getTranslation(uploadMetadata.type)
      : '';
    const patient = uploadMetadata.patientId ? await this._patientService.find(uploadMetadata.patientId) : '';
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(currentDate.getDate()).padStart(2, '0');

    let filename;
    if (patient) {
      filename = `${attachmentType}_${patient.firstName}_${patient.lastName}_${year}-${month}-${day}.${fileExtension}`;
    } else {
      filename = `${year}-${month}-${day}.${fileExtension}`;
    }

    return { blob, uploadMetadata, filename, mime };
  }

  private _getUploadReadyMetadata(metadata: CreateMetadata): UploadReadyMetadata {
    const newMetadata = cloneDeep(metadata) as unknown as UploadReadyMetadata;
    newMetadata.uniqueId = metadata.uniqueId ?? `${ATTACHMENT_LOCAL_PREFIX}${v4()}`;
    delete newMetadata['name'];
    return newMetadata;
  }

  public async createAttachmentForState(
    patient: IPatient,
    createResult: UploadResult,
    meta: CreateMetadata
  ): Promise<Partial<AttachmentDto & { displayName: string }>> {
    const attachmentForState = {
      _id: createResult.data?.id,
      displayName: '',
      filename: meta.name,
      metadata: {
        ...meta,
        createdAt: new Date(),
      },
      uploadDate: new Date(),
      loading: createResult.loading,
    };
    const displayName = await this.getDisplayFileName(patient, attachmentForState as any);
    attachmentForState.displayName = displayName;
    attachmentForState.filename = displayName;
    return attachmentForState as Partial<AttachmentDto & { displayName: string }>;
  }

  public async transformBlob(data: Blob, returnValue: 'blobUrl' | 'blob' | 'base64'): Promise<string | Blob> {
    /**
     * It's strange to accept data == null and return undefined,
     * but dependend code is using undefined to check if the data is available,
     * so currently no refactoring is done
     */
    if (data) {
      switch (returnValue) {
        case 'blobUrl':
          return Promise.resolve(URL.createObjectURL(data as Blob));
        case 'blob':
          return Promise.resolve(data);
        case 'base64':
          return new Promise(resolve => {
            const reader = new FileReader();
            reader.readAsDataURL(data as Blob);
            reader.onload = () => {
              resolve(reader.result as string);
            };
          });
      }
    }
  }
}
